import type { TableConfig } from "./types";

/**
 * Keys that must NOT travel between connectors (SIM-3182). Every other key
 * describes the TABLE; `overrides` describes the CLIENT (PowerSchool scoping),
 * and an inherited scope is wrong data silently rather than a visible error.
 */
const NON_PORTABLE_KEYS = ["overrides"];

/** Case-insensitive, whitespace-tolerant table-name key. */
export function tableNameKey(name: unknown): string {
  return typeof name === "string" ? name.trim().toLowerCase() : "";
}

/** One source entry, stripped of everything client-specific. */
export function copyTableEntry(entry: Record<string, unknown>): TableConfig {
  const copy = { ...entry };
  for (const key of NON_PORTABLE_KEYS) delete copy[key];
  return copy as unknown as TableConfig;
}

/**
 * The entries to append for a given selection. Names already configured on the
 * target are skipped — the picker disables them, and this is the second guard
 * so a stale dialog can never duplicate a row.
 */
export function copyTableEntries(
  sourceTables: Record<string, unknown>[],
  selectedNames: string[],
  existingTables: TableConfig[],
): TableConfig[] {
  const taken = new Set(existingTables.map((t) => tableNameKey(t.name)));
  const selected = new Set(selectedNames.map(tableNameKey));
  const out: TableConfig[] = [];
  for (const entry of sourceTables) {
    const key = tableNameKey(entry.name);
    if (!key || !selected.has(key) || taken.has(key)) continue;
    taken.add(key);
    out.push(copyTableEntry(entry));
  }
  return out;
}
